import classNames from "classnames";
import {
  CharacterPartType,
} from "../../../dto/CharacterPartDTO";
import { CharacterPartGenderedPairDTO } from "../../../dto/CharacterPartGenderedPairDTO";
import {
  selectCachedCharacterPartRenderByName,
  selectSelectedCharacterPart,
  setCachedCharacterPartRender,
  setSelectedCharacterPart,
} from "../../../app/slices/CharacterPartsSlice";
import { useDispatch, useSelector } from "react-redux";
import { useEffect, useMemo, useState } from "react";
import ClothesIcon from "../../../icons/ClothesIcon";
import { selectCurrentOrganization } from "../../../app/slices/UserSlice";
import { CharacterPartService } from "../../../services/CharacterPartService";
import { RootState } from "../../../app/Store";

export default function CharacterPartLibraryCard(props: {
  characterPart: CharacterPartGenderedPairDTO;
  onSubmit: () => void;
}) {
  const { characterPart } = { ...props };
  const selectedCharacterPart = useSelector(selectSelectedCharacterPart);
  const currentOrganization = useSelector(selectCurrentOrganization);
  const renderUri = useSelector((state: RootState) =>
    selectCachedCharacterPartRenderByName(state, characterPart.name)
  );
  const dispatch = useDispatch();
  const [isFetchingRender, setIsFetchingRender] = useState(false);
  const [isImageLoaded, setIsImageLoaded] = useState(false);
  const [hasImageError, setHasImageError] = useState(false);
  const isSelected = useMemo(() => {
    const result =
      selectedCharacterPart && selectedCharacterPart?.name === characterPart.name;

    return result;
  }, [selectedCharacterPart, characterPart]);

  useEffect(() => {
    setIsImageLoaded(false);
    setHasImageError(false);
  }, [renderUri]);

  useEffect(() => {
    if (!currentOrganization?.id) return;
    if (characterPart.name === "unloaded") return;
    if (renderUri) return;

    const loadRender = async () => {
      setIsFetchingRender(true);
      const render = await CharacterPartService.GetCharacterPartGenderedPairRender(
        currentOrganization.id,
        characterPart.name
      );
      if (!render?.downloadUri) {
        setIsFetchingRender(false);
        return;
      }

      dispatch(
        setCachedCharacterPartRender({
          characterPartName: characterPart.name,
          downloadUri: render.downloadUri,
        })
      );
      setIsFetchingRender(false);
    };

    loadRender();
  }, [characterPart.name, currentOrganization?.id, dispatch, renderUri]);

  const onClick = () => {
    dispatch(setSelectedCharacterPart(characterPart));
  };

  const onDoubleClick = () => {
    if (selectedCharacterPart?.name !== characterPart.name)
      return;
    props.onSubmit();
  };

  const showSkeleton =
    characterPart.name !== "unloaded" &&
    ((isFetchingRender && !renderUri) || (!!renderUri && !isImageLoaded && !hasImageError));

  const fallbackPreview = (
    <div className="flex h-full w-full items-center justify-center text-[48px] text-creator-text">
      <ClothesIcon size={48} />
    </div>
  );

  const top = (
    <div className="relative flex flex-1 justify-center items-center w-full overflow-hidden bg-interaction-softBackground rounded-t-[5px] select-none">
      {showSkeleton && (
        <div className="absolute inset-0 animate-pulse bg-creator-surface" />
      )}
      {renderUri && !hasImageError ? (
        <img
          className={classNames(
            "h-full w-full object-contain rounded-t-[5px] p-[8px] transition-opacity",
            {
              "opacity-0": !isImageLoaded,
              "opacity-100": isImageLoaded,
            }
          )}
          src={renderUri}
          alt={characterPart.name}
          onLoad={() => setIsImageLoaded(true)}
          onError={() => {
            setHasImageError(true);
            setIsImageLoaded(false);
          }}
        />
      ) : (
        !showSkeleton && fallbackPreview
      )}
      {!renderUri && !showSkeleton && fallbackPreview}
      {renderUri && hasImageError && fallbackPreview}
    </div>
  );
  const bottom = (
    <div className="h-[72px] px-[20px] py-[16px] flex flex-col select-none">
      <div className="font-bold truncate text-[16px]">{characterPart.name}</div>
      <div className="text-[14px] text-creator-text-sub">
        {CharacterPartType[characterPart.characterPartType]}
      </div>
    </div>
  );

  return (
    <div
      className={classNames(
        "flex flex-col w-full border-2 border-solid cursor-pointer h-[200px] rounded-[5px]",
        {
          "border-creator-border": !isSelected,
          "border-creator-primary": isSelected,
        }
      )}
      onClick={onClick}
      onDoubleClick={onDoubleClick}
    >
      {top}
      {bottom}
    </div>
  );
}
